<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  name: '',
  baseUrl: '',
  apiKey: '',
  tenant: '',
  wssUrl: '',
  sipDomain: '',
  extensionNumber: '',
  deviceUser: '',
  devicePassword: '',
  phoneNumber: '',
  outboundCallerId: '',
  amiHost: '',
  amiPort: '',
  amiUsername: '',
  amiSecret: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  baseUrl: { required },
  apiKey: { required },
  wssUrl: { required },
  extensionNumber: { required },
  deviceUser: { required },
  devicePassword: { required },
  amiUsername: { required },
  amiSecret: { required },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  baseUrl: v$.value.baseUrl?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.BASE_URL.ERROR')
    : '',
  apiKey: v$.value.apiKey?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.API_KEY.ERROR')
    : '',
  wssUrl: v$.value.wssUrl?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.WSS_URL.ERROR')
    : '',
  extensionNumber: v$.value.extensionNumber?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.EXTENSION_NUMBER.ERROR')
    : '',
  deviceUser: v$.value.deviceUser?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.DEVICE_USER.ERROR')
    : '',
  devicePassword: v$.value.devicePassword?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.DEVICE_PASSWORD.ERROR')
    : '',
  amiUsername: v$.value.amiUsername?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.AMI_USERNAME.ERROR')
    : '',
  amiSecret: v$.value.amiSecret?.$error
    ? t('INBOX_MGMT.ADD.VITALPBX.AMI_SECRET.ERROR')
    : '',
}));

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const channel = await store.dispatch('inboxes/createChannel', {
      name: state.name.trim() || `VitalPBX (${state.extensionNumber})`,
      channel: {
        type: 'vitalpbx',
        base_url: state.baseUrl.trim(),
        api_key: state.apiKey.trim(),
        tenant: state.tenant.trim() || undefined,
        wss_url: state.wssUrl.trim(),
        sip_domain: state.sipDomain.trim() || undefined,
        extension_number: state.extensionNumber.trim(),
        device_user: state.deviceUser.trim(),
        device_password: state.devicePassword.trim(),
        phone_number: state.phoneNumber.trim() || undefined,
        outbound_caller_id: state.outboundCallerId.trim() || undefined,
        ami_host: state.amiHost.trim() || undefined,
        ami_port: state.amiPort.trim() || undefined,
        ami_username: state.amiUsername.trim() || undefined,
        ami_secret: state.amiSecret.trim() || undefined,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      t('INBOX_MGMT.ADD.VITALPBX.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VITALPBX.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VITALPBX.DESC')"
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0 max-w-2xl"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.name"
        :label="t('INBOX_MGMT.ADD.VITALPBX.INBOX_NAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VITALPBX.INBOX_NAME.PLACEHOLDER')"
      />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.baseUrl"
          :label="t('INBOX_MGMT.ADD.VITALPBX.BASE_URL.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.BASE_URL.PLACEHOLDER')"
          :message="formErrors.baseUrl"
          :message-type="formErrors.baseUrl ? 'error' : 'info'"
          @blur="v$.baseUrl?.$touch"
        />

        <Input
          v-model="state.apiKey"
          type="password"
          :label="t('INBOX_MGMT.ADD.VITALPBX.API_KEY.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.API_KEY.PLACEHOLDER')"
          :message="formErrors.apiKey"
          :message-type="formErrors.apiKey ? 'error' : 'info'"
          @blur="v$.apiKey?.$touch"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.tenant"
          :label="t('INBOX_MGMT.ADD.VITALPBX.TENANT.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.TENANT.PLACEHOLDER')"
        />

        <Input
          v-model="state.wssUrl"
          :label="t('INBOX_MGMT.ADD.VITALPBX.WSS_URL.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.WSS_URL.PLACEHOLDER')"
          :message="formErrors.wssUrl"
          :message-type="formErrors.wssUrl ? 'error' : 'info'"
          @blur="v$.wssUrl?.$touch"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.sipDomain"
          :label="t('INBOX_MGMT.ADD.VITALPBX.SIP_DOMAIN.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.SIP_DOMAIN.PLACEHOLDER')"
        />

        <Input
          v-model="state.extensionNumber"
          :label="t('INBOX_MGMT.ADD.VITALPBX.EXTENSION_NUMBER.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VITALPBX.EXTENSION_NUMBER.PLACEHOLDER')
          "
          :message="formErrors.extensionNumber"
          :message-type="formErrors.extensionNumber ? 'error' : 'info'"
          @blur="v$.extensionNumber?.$touch"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.deviceUser"
          :label="t('INBOX_MGMT.ADD.VITALPBX.DEVICE_USER.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.DEVICE_USER.PLACEHOLDER')"
          :message="formErrors.deviceUser"
          :message-type="formErrors.deviceUser ? 'error' : 'info'"
          @blur="v$.deviceUser?.$touch"
        />

        <Input
          v-model="state.devicePassword"
          type="password"
          :label="t('INBOX_MGMT.ADD.VITALPBX.DEVICE_PASSWORD.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VITALPBX.DEVICE_PASSWORD.PLACEHOLDER')
          "
          :message="formErrors.devicePassword"
          :message-type="formErrors.devicePassword ? 'error' : 'info'"
          @blur="v$.devicePassword?.$touch"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.phoneNumber"
          :label="t('INBOX_MGMT.ADD.VITALPBX.PHONE_NUMBER.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.PHONE_NUMBER.PLACEHOLDER')"
        />

        <Input
          v-model="state.outboundCallerId"
          :label="t('INBOX_MGMT.ADD.VITALPBX.OUTBOUND_CALLER_ID.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VITALPBX.OUTBOUND_CALLER_ID.PLACEHOLDER')
          "
        />
      </div>

      <div class="pt-2">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.VITALPBX.AMI_SECTION_TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.VITALPBX.AMI_SECTION_SUBTITLE') }}
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.amiHost"
          :label="t('INBOX_MGMT.ADD.VITALPBX.AMI_HOST.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.AMI_HOST.PLACEHOLDER')"
        />

        <Input
          v-model="state.amiPort"
          :label="t('INBOX_MGMT.ADD.VITALPBX.AMI_PORT.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.AMI_PORT.PLACEHOLDER')"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          v-model="state.amiUsername"
          :label="t('INBOX_MGMT.ADD.VITALPBX.AMI_USERNAME.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.AMI_USERNAME.PLACEHOLDER')"
          :message="formErrors.amiUsername"
          :message-type="formErrors.amiUsername ? 'error' : 'info'"
          @blur="v$.amiUsername?.$touch"
        />

        <Input
          v-model="state.amiSecret"
          type="password"
          :label="t('INBOX_MGMT.ADD.VITALPBX.AMI_SECRET.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VITALPBX.AMI_SECRET.PLACEHOLDER')"
          :message="formErrors.amiSecret"
          :message-type="formErrors.amiSecret ? 'error' : 'info'"
          @blur="v$.amiSecret?.$touch"
        />
      </div>

      <div class="mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VITALPBX.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
